/**
 * MMS Art Studio — Landing Page (iter 193 redesign)
 *
 * Goals:
 *   • Clean, simple, conversion-focused — drives visitors into the Community Portal
 *   • Premium Liquid Glass / Glassmorphism aesthetic with gold + cyan accents
 *   • Dark mode default (cinematic) with elegant Light mode
 *   • Single-file structure for readability — no premature splitting
 *
 * Sections:
 *   1. Navbar     — logo / center nav / Login + theme toggle
 *   2. Hero       — cinematic background, headline, subheadline
 *   3. Portals    — 3 cards, Community is the hero (largest, double-wide)
 *   4. Instructors— 6 cards from /api/instructors/directory
 *   5. Why MMS    — 3 benefit pills
 *   6. Testimonials— horizontal scroll on mobile, 3-col on desktop
 *   7. Footer     — secondary links
 */
import { useEffect, useMemo, useRef, useState, Suspense, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sun, Moon, Radio, GraduationCap, Mic2, ArrowRight, Sparkles,
  Heart, Users, Music, Star, ChevronRight, Globe, Award, ShieldCheck,
  Play, X as XIcon, Clock, DollarSign, ChevronDown, ShieldAlert, LogIn,
  BookOpen, Layers, MessageSquare, User as UserIcon,
  CheckCircle2, ArrowUp,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import mmsLogo from '../assets/mms-logo-dark.svg';
import mmsLogoLight from '../assets/mms-logo-light.svg';
import Seo from '../components/Seo';

const BelowFold = lazy(() => import('./landing/BelowFold'));
// Auth modals only load on demand (sign-in / sign-up click).
const AuthModal = lazy(() => import('../components/AuthModal'));
const CommunityAuthModal = lazy(() => import('../components/CommunityAuthModal'));

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const stagger = { animate: { transition: { staggerChildren: 0.08 } } };
const fadeUp = {
  initial: { opacity: 0, y: 22, filter: 'blur(6px)' },
  animate: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

// ───────────────────────────────────────────────────────────────────
// Cinematic backdrops — dark uses the rich purple/amber concert shot,
// light uses an airy concert hall so the page never feels washed out.
// Both are wide enough to look great as background-cover on 4K screens.
// Self-hosted WebP hero backgrounds (3 sizes) — the 2400px Unsplash JPEGs
// were ~630KB each and the page's LCP element.
const HERO_BG_DARK = '/hero/dark-1600.webp';
const HERO_BG_LIGHT = '/hero/light-1600.webp';
const HERO_SRCSET = {
  [HERO_BG_DARK]: '/hero/dark-960.webp 960w, /hero/dark-1600.webp 1600w, /hero/dark-2400.webp 2400w',
  [HERO_BG_LIGHT]: '/hero/light-960.webp 960w, /hero/light-1600.webp 1600w, /hero/light-2400.webp 2400w',
};

const PORTALS_BG = {
  community: 'https://images.unsplash.com/photo-1571974599782-87624638275e?w=1200&q=80&auto=format&fit=crop',
  student:   'https://images.unsplash.com/photo-1520523839897-bd0b52f945a0?w=1200&q=80&auto=format&fit=crop',
  instructor:'https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=1200&q=80&auto=format&fit=crop',
};

// 21 disciplines (synced with backend SCHOOL_COURSES), grouped by category.
const DISCIPLINE_GROUPS = [
  {
    key: 'instruments',
    label: 'Instruments',
    accent: '#fcd34d',
    items: [
      'Piano', 'Guitar', 'Bass', 'Drums',
      'Trumpet', 'Trombone',
      'Flute', 'Clarinet', 'Oboe',
      'Soprano Saxophone', 'Alto Saxophone', 'Tenor Saxophone', 'Baritone Saxophone',
    ],
  },
  {
    key: 'vocal',
    label: 'Vocal',
    accent: '#fda4af',
    items: ['Vocal'],
  },
  {
    key: 'theory',
    label: 'Theory',
    accent: '#7dd3fc',
    items: ['Music Theory', 'Jazz Theory', 'Improvisation', 'Arrangement'],
  },
  {
    key: 'production',
    label: 'Production',
    accent: '#c4b5fd',
    items: ['Studio Production', 'AI Creative Music', 'Live Production'],
  },
];

// ═══════════════════════════════════════════════════════════════════
// 0. Premium theme toggle — frosted glass pill with sliding sun/moon
// ═══════════════════════════════════════════════════════════════════
function ThemeSwitcher() {
  const { theme, setTheme } = useTheme();
  const isDark = theme === 'dark';
  return (
    <button
      type="button"
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      data-testid="landing-theme-toggle"
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      className="relative w-[68px] h-9 rounded-full overflow-hidden transition-all duration-300 hover:scale-[1.04] active:scale-95"
      style={{
        background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)'}`,
        backdropFilter: 'blur(14px) saturate(1.4)',
        WebkitBackdropFilter: 'blur(14px) saturate(1.4)',
        boxShadow: isDark
          ? 'inset 0 1px 0 rgba(255,255,255,0.06), 0 6px 18px rgba(0,0,0,0.35)'
          : 'inset 0 1px 0 rgba(255,255,255,0.6), 0 6px 18px rgba(0,0,0,0.08)',
      }}
    >
      {/* Sliding indicator */}
      <motion.div
        layout
        transition={{ type: 'spring', damping: 22, stiffness: 320 }}
        className="absolute top-1 w-7 h-7 rounded-full flex items-center justify-center"
        style={{
          left: isDark ? 3 : 34,
          background: isDark
            ? 'linear-gradient(135deg, #1a1a1a, #050505)'
            : 'linear-gradient(135deg, #ffffff, #f1f1f1)',
          border: `1px solid ${isDark ? 'rgba(212,175,55,0.35)' : 'rgba(0,0,0,0.06)'}`,
          boxShadow: isDark
            ? '0 4px 12px rgba(0,0,0,0.5), inset 0 1px 0 rgba(212,175,55,0.15)'
            : '0 4px 12px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.9)',
        }}
      >
        <AnimatePresence mode="wait">
          {isDark ? (
            <motion.span key="moon" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
              <Moon size={13} className="text-amber-300" />
            </motion.span>
          ) : (
            <motion.span key="sun" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.2 }}>
              <Sun size={13} className="text-amber-500" />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.div>
      {/* Faint static icons on either side for orientation */}
      <Sun size={11} className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ opacity: isDark ? 0.18 : 0, color: '#fcd34d' }} />
      <Moon size={11} className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ opacity: isDark ? 0 : 0.18, color: '#7dd3fc' }} />
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Disciplines dropdown — 4 grouped categories, all 21 disciplines
// Vertical multi-column panel on desktop, stacked accordion-ish list
// with horizontal-scrolling pills per group on mobile.
// Every chip routes to /instructors?course=<discipline>.
// ═══════════════════════════════════════════════════════════════════
function DisciplinesDropdown({ onPick }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('touchstart', onClick, { passive: true });
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('touchstart', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pick = (course) => {
    setOpen(false);
    onPick(course);
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="nav-link-disciplines"
        className="px-3 sm:px-4 py-2 text-[13px] font-semibold rounded-full transition-all duration-200 flex items-center gap-1.5"
        style={{
          color: 'hsl(var(--foreground))',
          fontFamily: 'Outfit, sans-serif',
          background: open ? 'rgba(212,175,55,0.14)' : 'var(--lp-surface)',
          border: '1px solid var(--lp-border-soft)',
        }}
        onMouseEnter={(e) => { if (!open) e.currentTarget.style.background = 'rgba(212,175,55,0.10)'; }}
        onMouseLeave={(e) => { if (!open) e.currentTarget.style.background = 'var(--lp-surface)'; }}
      >
        <Layers size={13} className="sm:hidden" />
        <span className="hidden sm:inline">Disciplines</span>
        <ChevronDown
          size={13}
          className="transition-transform duration-200"
          style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', opacity: 0.7 }}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            key="disciplines-menu"
            initial={{ opacity: 0, y: -8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.97 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            role="menu"
            data-testid="disciplines-menu"
            className="fixed sm:absolute left-2 right-2 sm:left-auto sm:right-0 top-[68px] sm:top-full sm:mt-3 origin-top-right rounded-2xl overflow-hidden z-50 sm:w-[640px] lg:w-[820px] max-w-[96vw]"
            style={{
              background: 'var(--lp-panel-bg)',
              border: '1px solid var(--lp-border)',
              boxShadow: '0 30px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(212,175,55,0.18), 0 0 36px rgba(212,175,55,0.10)',
              backdropFilter: 'blur(28px) saturate(1.6)',
              WebkitBackdropFilter: 'blur(28px) saturate(1.6)',
            }}
          >
            <div
              className="px-5 pt-4 pb-2 flex items-center gap-1.5"
              style={{
                fontFamily: 'Outfit, sans-serif',
                fontSize: 10,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: 'rgba(212,175,55,0.95)',
              }}
            >
              <BookOpen size={11} />
              <span>21 disciplines · taught by verified pros</span>
            </div>

            {/* Desktop: Instruments is double-wide so its 13 chips wrap nicely.
                Mobile: groups stack vertically; chips wrap to multiple rows (no horizontal scroll). */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-x-4 gap-y-4 px-3 sm:px-5 pt-2 pb-4">
              {DISCIPLINE_GROUPS.map((g) => (
                <div
                  key={g.key}
                  className={`min-w-0 ${g.key === 'instruments' ? 'lg:col-span-2' : 'lg:col-span-1'}`}
                >
                  <div
                    className="px-2 pb-2 flex items-center gap-2"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ background: g.accent, boxShadow: `0 0 8px ${g.accent}` }}
                    />
                    <span
                      className="text-[10px] font-bold uppercase tracking-[0.22em]"
                      style={{ color: g.accent }}
                    >
                      {g.label}
                    </span>
                    <span
                      className="text-[10px] tabular-nums"
                      style={{ color: 'var(--lp-text-on-glass-faint)' }}
                    >
                      {g.items.length}
                    </span>
                  </div>

                  {/* Items: flex-wrap on both mobile and desktop so the full list is always visible — no cutoff. */}
                  <div className="flex flex-wrap gap-1.5 px-1 lg:px-0">
                    {g.items.map((item) => (
                      <button
                        key={item}
                        type="button"
                        role="menuitem"
                        onClick={() => pick(item)}
                        data-testid={`discipline-${item.toLowerCase().replace(/\s+/g, '-')}`}
                        className="px-3 py-2 rounded-lg text-[12.5px] font-medium transition-all duration-150"
                        style={{
                          background: 'var(--lp-surface)',
                          border: '1px solid var(--lp-border-soft)',
                          color: 'var(--lp-text-on-glass)',
                          fontFamily: 'Outfit, sans-serif',
                          whiteSpace: 'nowrap',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = `${g.accent}1f`;
                          e.currentTarget.style.borderColor = `${g.accent}66`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'var(--lp-surface)';
                          e.currentTarget.style.borderColor = 'var(--lp-border-soft)';
                        }}
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div
              className="px-5 py-3 border-t flex items-center justify-between gap-3"
              style={{
                borderColor: 'var(--lp-border-soft)',
                fontFamily: 'Outfit, sans-serif',
                fontSize: 11,
                color: 'var(--lp-text-on-glass-soft)',
              }}
            >
              <span>Pick a discipline to meet your instructor.</span>
              <button
                type="button"
                onClick={() => pick('')}
                data-testid="disciplines-browse-all"
                className="inline-flex items-center gap-1 text-[11px] font-semibold transition-all duration-150 hover:gap-2"
                style={{ color: '#b8942e' }}
              >
                Browse all <ArrowRight size={11} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Login dropdown — lists all four portals (Student / Instructor / Community / Admin)
// ═══════════════════════════════════════════════════════════════════
const LOGIN_PORTALS = [
  {
    role: 'community',
    label: 'Community',
    sub: 'Live, jam, connect',
    icon: Radio,
    accent: '#fcd34d',
    glow: 'rgba(212,175,55,0.18)',
  },
  {
    role: 'student',
    label: 'Student',
    sub: 'Learn with verified pros',
    icon: GraduationCap,
    accent: '#7dd3fc',
    glow: 'rgba(125,211,252,0.18)',
  },
  {
    role: 'instructor',
    label: 'Instructor',
    sub: 'Teach & earn',
    icon: Mic2,
    accent: '#fda4af',
    glow: 'rgba(253,164,175,0.18)',
  },
  {
    role: 'admin',
    label: 'Admin',
    sub: 'Studio operations',
    icon: ShieldAlert,
    accent: '#c4b5fd',
    glow: 'rgba(196,181,253,0.18)',
  },
];

function LoginDropdown({ onPortalLogin }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('touchstart', onClick, { passive: true });
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('touchstart', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handlePick = (role) => {
    setOpen(false);
    onPortalLogin(role);
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="landing-login-btn"
        className="px-4 sm:px-5 py-2 text-[13px] font-semibold rounded-full transition-all duration-200 hover:scale-[1.03] active:scale-95 flex items-center gap-1.5"
        style={{
          background: open ? 'rgba(212,175,55,0.14)' : 'var(--lp-surface)',
          border: open ? '1px solid rgba(212,175,55,0.45)' : '1px solid var(--lp-border)',
          color: 'hsl(var(--foreground))',
          fontFamily: 'Outfit, sans-serif',
          backdropFilter: 'blur(10px)',
        }}
      >
        Login
        <ChevronDown
          size={14}
          className="transition-transform duration-200"
          style={{
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            opacity: 0.7,
          }}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            key="login-menu"
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.97 }}
            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            role="menu"
            data-testid="landing-login-menu"
            className="absolute right-0 mt-3 w-[280px] sm:w-[300px] origin-top-right rounded-2xl overflow-hidden z-50"
            style={{
              background: 'var(--lp-panel-bg)',
              border: '1px solid var(--lp-border)',
              boxShadow: '0 24px 60px rgba(0,0,0,0.25), 0 0 0 1px rgba(212,175,55,0.05)',
              backdropFilter: 'blur(28px) saturate(1.6)',
              WebkitBackdropFilter: 'blur(28px) saturate(1.6)',
            }}
          >
            <div
              className="px-4 pt-3 pb-2 flex items-center gap-1.5"
              style={{
                fontFamily: 'Outfit, sans-serif',
                fontSize: 10,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: 'rgba(212,175,55,0.95)',
              }}
            >
              <LogIn size={11} />
              <span>Sign in to a portal</span>
            </div>

            <ul className="px-2 pb-2">
              {LOGIN_PORTALS.map((p) => {
                const Icon = p.icon;
                return (
                  <li key={p.role}>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => handlePick(p.role)}
                      data-testid={`login-menu-${p.role}`}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-150 group"
                      style={{
                        background: 'transparent',
                        color: 'var(--lp-text-on-glass)',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = p.glow;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <span
                        className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-transform duration-200 group-hover:scale-110"
                        style={{
                          background: `linear-gradient(135deg, ${p.accent}22, ${p.accent}10)`,
                          border: `1px solid ${p.accent}40`,
                          color: p.accent,
                        }}
                      >
                        <Icon size={16} />
                      </span>
                      <span className="flex-1 text-left">
                        <span
                          className="block text-[14px] font-semibold leading-tight"
                          style={{ fontFamily: 'Outfit, sans-serif' }}
                        >
                          {p.label}
                        </span>
                        <span
                          className="block text-[11px] mt-0.5"
                          style={{
                            color: 'var(--lp-text-on-glass-soft)',
                            fontFamily: 'Outfit, sans-serif',
                          }}
                        >
                          {p.sub}
                        </span>
                      </span>
                      <ChevronRight
                        size={14}
                        className="shrink-0 transition-transform duration-200 group-hover:translate-x-0.5"
                        style={{ color: p.accent, opacity: 0.85 }}
                      />
                    </button>
                  </li>
                );
              })}
            </ul>

            <div
              className="px-4 py-2.5 border-t"
              style={{
                borderColor: 'var(--lp-border-soft)',
                fontFamily: 'Outfit, sans-serif',
                fontSize: 11,
                color: 'var(--lp-text-on-glass-soft)',
              }}
            >
              New here? Pick a portal — registration follows.
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 1. Navbar
// ═══════════════════════════════════════════════════════════════════
function Navbar({ logo, onPortalLogin, onPortalClick, onDiscipline }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const navLinks = [
    // 'Home' removed (Jul 2026) — the logo already links to #hero, so a Home
    // item in the desktop nav was redundant.
    // 'Community' link removed — users have two other ways to reach the
    // Community portal: (a) Login dropdown → Community, (b) the Community
    // hero card on the landing page. A third entry point is redundant.
  ];
  return (
    <header
      className="fixed top-0 inset-x-0 z-50 transition-all duration-300"
      style={{
        background: scrolled ? 'var(--lp-nav-bg-scrolled)' : 'transparent',
        borderBottom: scrolled ? '1px solid var(--lp-nav-border)' : '1px solid transparent',
        backdropFilter: scrolled ? 'blur(20px) saturate(1.4)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(20px) saturate(1.4)' : 'none',
      }}
      data-testid="landing-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-[72px] flex items-center justify-between gap-4">
        {/* Logo */}
        <a href="#hero" className="flex items-center gap-2.5 shrink-0" data-testid="landing-logo">
          <img src={logo} alt="MMS Art Studio" width={140} height={36} className="h-8 sm:h-9 w-auto brand-logo-img" />
        </a>
        {/* Center nav (desktop only) */}
        <nav className="hidden md:flex items-center gap-1" data-testid="landing-nav-center">
          {navLinks.map(l => (
            <a
              key={l.label}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="px-4 py-2 text-[13px] font-semibold rounded-full transition-all duration-200"
              style={{
                color: l.primary ? 'var(--lp-gold)' : 'hsl(var(--foreground))',
                fontFamily: 'Outfit, sans-serif',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(212,175,55,0.08)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              {l.label}
            </a>
          ))}
        </nav>
        {/* Right cluster */}
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          <DisciplinesDropdown onPick={onDiscipline} />
          <LoginDropdown onPortalLogin={onPortalLogin} />
          <ThemeSwitcher />
        </div>
      </div>
    </header>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Watch-a-Trailer lightbox + trigger pill
// ═══════════════════════════════════════════════════════════════════
// Trailer source: override via REACT_APP_TRAILER_URL in .env, otherwise
// fall back to a free Coverr.co cinematic music clip.
const TRAILER_URL = process.env.REACT_APP_TRAILER_URL
  || 'https://cdn.coverr.co/videos/coverr-musician-playing-an-electric-guitar-2496/1080p.mp4';
const TRAILER_POSTER = 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=1600&q=80&auto=format&fit=crop';

function TrailerLightbox({ onClose }) {
  // Lock body scroll while open
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = original;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8"
      style={{ background: 'rgba(2,2,4,0.92)', backdropFilter: 'blur(18px) saturate(1.4)' }}
      onClick={onClose}
      data-testid="trailer-lightbox"
    >
      <motion.div
        initial={{ scale: 0.92, y: 20, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.94, opacity: 0 }}
        transition={{ type: 'spring', damping: 26, stiffness: 280 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-5xl rounded-[28px] overflow-hidden"
        style={{
          background: 'rgba(8,8,12,0.96)',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 40px 120px rgba(0,0,0,0.65), inset 0 1px 0 rgba(255,255,255,0.06)',
        }}
      >
        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close trailer"
          data-testid="trailer-close"
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full flex items-center justify-center transition hover:scale-105 active:scale-95"
          style={{
            background: 'rgba(0,0,0,0.65)',
            border: '1px solid rgba(255,255,255,0.12)',
            color: '#fff',
            backdropFilter: 'blur(10px)',
          }}
        >
          <XIcon size={15} />
        </button>
        {/* Video */}
        <div className="aspect-video bg-black">
          <video
            data-testid="trailer-video"
            src={TRAILER_URL}
            poster={TRAILER_POSTER}
            controls
            autoPlay
            playsInline
            className="w-full h-full object-cover"
          >
            Your browser does not support HTML5 video.
          </video>
        </div>
        {/* Caption strip */}
        <div className="px-5 sm:px-7 py-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-amber-300/85 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              The MMS Trailer
            </p>
            <p className="text-sm sm:text-base font-bold leading-tight" style={{ fontFamily: 'Playfair Display, serif', color: '#fff' }}>
              60 seconds inside the world&apos;s most musician-first community.
            </p>
          </div>
          <a
            href="/community"
            className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12px] font-bold transition hover:scale-[1.03]"
            data-testid="trailer-cta"
            style={{
              background: 'linear-gradient(135deg, #E5C158, #D4AF37, #b8942e)',
              color: '#0a0a0a',
              boxShadow: '0 4px 14px rgba(212,175,55,0.35)',
              fontFamily: 'Outfit, sans-serif',
            }}
          >
            Enter Community <ArrowRight size={11} />
          </a>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// LiveCountBanner — only shown when /api/live/active has streams.
// Tells visitors "the party is already happening" and routes them
// straight to the Portals section / Community card.
// ═══════════════════════════════════════════════════════════════════
function LiveCountBanner({ onClick }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const r = await fetch(`${API}/live/active`);
        if (!r.ok || !alive) return;
        const d = await r.json();
        setCount(Array.isArray(d) ? d.length : 0);
      } catch { /* swallow */ }
    };
    poll();
    const id = setInterval(poll, 30000);  // refresh every 30s
    return () => { alive = false; clearInterval(id); };
  }, []);
  if (count <= 0) return null;
  return (
    <motion.button
      type="button"
      onClick={onClick}
      data-testid="landing-live-count-banner"
      initial={{ opacity: 0, y: -16, scale: 0.94 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ scale: 1.04 }}
      whileTap={{ scale: 0.97 }}
      className="absolute z-20 left-1/2 -translate-x-1/2 inline-flex items-center gap-2.5 px-4 py-2 rounded-full font-semibold transition-shadow"
      style={{
        top: 'calc(env(safe-area-inset-top, 0px) + 88px)',
        background: 'rgba(244,63,94,0.14)',
        border: '1px solid rgba(244,63,94,0.45)',
        backdropFilter: 'blur(18px) saturate(1.6)',
        WebkitBackdropFilter: 'blur(18px) saturate(1.6)',
        boxShadow: '0 8px 32px rgba(244,63,94,0.25), inset 0 1px 0 rgba(255,255,255,0.06)',
        color: '#fda4af',
        fontFamily: 'Outfit, sans-serif',
      }}
    >
      {/* Pulsing red dot */}
      <span className="relative flex w-2.5 h-2.5">
        <motion.span
          animate={{ scale: [1, 1.7, 1], opacity: [0.7, 0, 0.7] }}
          transition={{ duration: 1.8, repeat: Infinity }}
          className="absolute inset-0 rounded-full bg-rose-400"
        />
        <span className="relative w-2.5 h-2.5 rounded-full bg-rose-400" />
      </span>
      <span className="text-[12px] sm:text-[13px] tracking-[0.04em]">
        <span className="font-bold" style={{ color: '#ffffff' }}>{count.toLocaleString()}</span>
        {' '}musician{count === 1 ? '' : 's'} live right now
      </span>
      <ChevronRight size={12} className="opacity-70" />
    </motion.button>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 2. Hero — minimal copy, big visual impact
// ═══════════════════════════════════════════════════════════════════
/* ════════════════════════════════════════════════════════════════════
   HERO — Premium destination grid (iter 238 redesign)
   • Minimal hero text: badge → H1 "Where Musicians Learn. Connect. Create."
     → equalizer accent → "One Platform. Endless Possibilities."
   • 4 destination cards inline (Community / Students / Instructors / Textbook
     Studio). Students + Instructors are visually paired by a connector dot.
   • Desktop: 4 columns. Mobile: 2x2 grid keeping the Students↔Instructors pair.
   ════════════════════════════════════════════════════════════════════ */

/* Card accent palette — pulled directly from the user's mockups */


/* Equalizer ornament between subhead and "One Platform" subtitle */
function EqualizerMark({ isDark }) {
  const fill = isDark ? '#fcd34d' : '#b8860b';
  return (
    <svg width="44" height="22" viewBox="0 0 44 22" className="inline-block" aria-hidden>
      {[2, 9, 16, 23, 30, 37].map((x, i) => (
        <motion.rect
          key={x} x={x} width="3.4" rx="1.4" fill={fill}
          initial={{ height: 4, y: 9 }}
          animate={{ height: [4, 14, 6, 18, 4][i % 5] || 8, y: [9, 4, 8, 2, 9][i % 5] || 7 }}
          transition={{ duration: 0.9, delay: i * 0.07, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }}
        />
      ))}
    </svg>
  );
}

/* Reusable hero destination card — editorial structure (whole-card CTA,
   index number, hairline action row) dressed in the page's warm brand
   tokens: --lp-gold is the single accent (icon, arrow, hover ring);
   type stays neutral so the gold reads as intent, not decoration. */
function HeroDestCard({ testid, index, icon: Icon, title, description, ctaLabel, onClick, isDark, badge }) {
  const ink = 'var(--lp-text-on-glass)';
  const soft = 'var(--lp-text-on-glass-soft)';
  const faint = 'var(--lp-text-on-glass-faint)';
  const gold = 'var(--lp-gold)';
  return (
    <motion.button
      type="button"
      onClick={onClick}
      data-testid={testid}
      whileHover={{ y: -4 }}
      whileTap={{ scale: 0.985 }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="group relative text-left rounded-2xl flex flex-col w-full h-full p-5 cursor-pointer focus:outline-none focus-visible:ring-2 overflow-hidden"
      style={{
        background: isDark
          ? 'linear-gradient(160deg, rgba(28,28,30,0.58) 0%, rgba(12,12,14,0.44) 100%)'
          : 'linear-gradient(160deg, rgba(255,255,255,0.72) 0%, rgba(255,251,240,0.48) 100%)',
        border: isDark ? '1px solid rgba(255,255,255,0.14)' : '1px solid rgba(255,255,255,0.75)',
        backdropFilter: isDark ? 'blur(22px) saturate(0.85)' : 'blur(22px) saturate(1.5)',
        WebkitBackdropFilter: isDark ? 'blur(22px) saturate(0.85)' : 'blur(22px) saturate(1.5)',
        boxShadow: isDark
          ? '0 16px 44px -16px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.14)'
          : '0 16px 44px -16px rgba(60,45,10,0.28), inset 0 1px 0 rgba(255,255,255,0.85)',
        color: ink,
      }}
    >
      {/* glass sheen — a soft diagonal light sweep across the pane */}
      <span aria-hidden className="absolute inset-0 rounded-2xl pointer-events-none"
            style={{ background: isDark
              ? 'linear-gradient(115deg, rgba(255,255,255,0.05) 0%, transparent 34%)'
              : 'linear-gradient(115deg, rgba(255,255,255,0.55) 0%, transparent 42%)' }} />

      {/* hover: warm gold ring */}
      <span aria-hidden className="absolute inset-0 rounded-2xl pointer-events-none transition-opacity duration-300 opacity-0 group-hover:opacity-100"
            style={{ boxShadow: isDark
              ? 'inset 0 0 0 1px rgba(252,211,77,0.5), 0 18px 40px -16px rgba(252,211,77,0.22)'
              : 'inset 0 0 0 1px rgba(154,102,6,0.45), 0 18px 40px -16px rgba(154,102,6,0.25)' }} />

      {/* top row: gold icon in a glass chip · index / badge */}
      <div className="relative flex items-center justify-between">
        <span className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{
                background: isDark ? 'rgba(252,211,77,0.12)' : 'rgba(154,102,6,0.10)',
                border: isDark ? '1px solid rgba(252,211,77,0.28)' : '1px solid rgba(154,102,6,0.25)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.18)',
              }}>
          <Icon size={17} strokeWidth={1.8} style={{ color: gold }} aria-hidden />
        </span>
        <span className="flex items-center gap-2">
          {badge && (
            <span className="px-1.5 py-[2px] rounded text-[8.5px] font-extrabold tracking-[0.14em] uppercase"
                  style={{
                    background: isDark ? 'rgba(252,211,77,0.14)' : 'rgba(154,102,6,0.12)',
                    border: `1px solid ${isDark ? 'rgba(252,211,77,0.5)' : 'rgba(154,102,6,0.5)'}`,
                    color: gold,
                  }}>
              {badge}
            </span>
          )}
          <span className="text-[10px] font-bold tabular-nums tracking-[0.18em]" style={{ color: faint, fontFamily: 'Outfit, sans-serif' }}>
            {index}
          </span>
        </span>
      </div>

      {/* title */}
      <h2 className="relative mt-4 text-[19px] sm:text-[21px] font-bold tracking-tight"
          style={{ fontFamily: 'Playfair Display, serif', color: ink, lineHeight: 1.12,
                   textShadow: isDark ? '0 1px 3px rgba(0,0,0,0.45)' : 'none' }}>
        {title}
      </h2>

      {/* description */}
      <p className="relative mt-1.5 text-[11.5px] sm:text-[12px] leading-[1.55] font-medium"
         style={{ color: soft, fontFamily: 'Outfit, sans-serif',
                  textShadow: isDark ? '0 1px 2px rgba(0,0,0,0.35)' : 'none' }}>
        {description}
      </p>

      <div className="flex-1 min-h-[12px]" />

      {/* CTA: solid gold pill — unmistakably a button */}
      <span className="relative mt-4 inline-flex items-center justify-center gap-1.5 w-full rounded-full py-2.5 text-[12px] font-bold transition-[filter] duration-300 group-hover:brightness-110"
            style={{
              background: gold,
              color: isDark ? '#1c1917' : '#fffdf7',
              fontFamily: 'Outfit, sans-serif',
              letterSpacing: '0.02em',
              boxShadow: isDark
                ? '0 8px 20px -8px rgba(252,211,77,0.45), inset 0 1px 0 rgba(255,255,255,0.35)'
                : '0 8px 20px -8px rgba(154,102,6,0.5), inset 0 1px 0 rgba(255,255,255,0.25)',
            }}>
        {ctaLabel}
        <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" aria-hidden />
      </span>
    </motion.button>
  );
}

function Hero({ onCommunity, onStartLearning, onInstructor }) {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [override, setOverride] = useState({ light: '', dark: '' });
  useEffect(() => {
    let alive = true;
    fetch(`${API}/hero-bg/public`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (alive && d) setOverride({ light: d.light_url || '', dark: d.dark_url || '' }); })
      .catch(() => { /* keep defaults */ });
    return () => { alive = false; };
  }, []);
  const bg = isDark ? (override.dark || HERO_BG_DARK) : (override.light || HERO_BG_LIGHT);

  return (
    <section
      id="hero"
      data-testid="landing-hero"
      className="relative isolate overflow-hidden flex flex-col"
      style={{ minHeight: '100vh', paddingTop: 72, paddingBottom: 16 }}
    >
      {/* Live count banner — only shows when streams are active */}
      <LiveCountBanner onClick={onCommunity} />

      {/* Background image with cinematic veil */}
      <div className="absolute inset-0 -z-10">
        <img
          src={bg}
          srcSet={HERO_SRCSET[bg]}
          sizes="100vw"
          width={1600}
          height={1067}
          alt=""
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            filter: isDark
              ? 'saturate(1.15) brightness(0.42) blur(3px)'
              : 'saturate(0.95) brightness(0.85) contrast(1.04) blur(3px)',
            transform: 'scale(1.04)',
          }}
        />
        <div className="absolute inset-0" style={{
          background: isDark
            // Mid stop deepened 0.40 → 0.54: bright hero photos were washing
            // out the white headline in the center band of the viewport.
            ? 'linear-gradient(180deg, rgba(7,7,11,0.80) 0%, rgba(7,7,11,0.54) 30%, rgba(7,7,11,0.92) 100%)'
            : 'linear-gradient(180deg, rgba(253,250,243,0.85) 0%, rgba(253,250,243,0.55) 30%, rgba(253,250,243,0.98) 100%)',
        }} />
        {/* Ambient gold glow */}
        <motion.div aria-hidden
          animate={{ opacity: isDark ? [0.35, 0.6, 0.35] : [0.30, 0.45, 0.30] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute top-1/4 -left-32 w-[520px] h-[520px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.55), transparent 65%)', filter: 'blur(56px)', willChange: 'opacity' }}
        />
      </div>

      {/* ── Hero text block (centered) ── */}
      <div className="relative max-w-5xl mx-auto px-5 text-center mb-5 sm:mb-7">
        {/* Overline */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[9px] font-bold uppercase tracking-[0.24em] mb-4"
          style={{
            background: isDark ? 'rgba(212,175,55,0.10)' : 'rgba(212,175,55,0.16)',
            border: `1px solid ${isDark ? 'rgba(212,175,55,0.32)' : 'rgba(212,175,55,0.48)'}`,
            color: isDark ? '#fcd34d' : '#8a6d1a',
            backdropFilter: 'blur(14px)',
          }}
          data-testid="hero-eyebrow"
        >
          Welcome to
        </motion.div>

        {/* School wordmark — the hero focal point */}
        <motion.h1
          initial={{ opacity: 0, y: 22, filter: 'blur(8px)' }}
          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
          transition={{ duration: 0.9, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
          data-testid="hero-headline"
          className="leading-[1.0] lp-hero-text"
          style={{
            fontFamily: "'Bodoni Moda', 'Playfair Display', serif",
            fontWeight: 700,
            fontSize: 'clamp(2.6rem, 8.5vw, 6rem)',
            letterSpacing: '-0.005em',
            color: 'hsl(var(--foreground))',
          }}
        >
          MMS{' '}
          <span style={{
            fontFamily: "'Playfair Display', 'Bodoni Moda', serif",
            fontStyle: 'italic',
            fontWeight: 600,
            color: 'var(--lp-gold)',
          }}>Art</span>{' '}
          Studio
        </motion.h1>

        {/* Tagline — smaller, subtle */}
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.18, ease: [0.22, 1, 0.36, 1] }}
          data-testid="hero-tagline"
          className="mt-3 font-medium tracking-tight lp-hero-text"
          style={{
            fontFamily: 'Playfair Display, serif',
            fontSize: 'clamp(1rem, 2.7vw, 1.65rem)',
            color: isDark ? 'rgba(255,255,255,0.80)' : 'rgba(30,25,15,0.82)',
          }}
        >
          Where Musicians{' '}
          <span style={{ color: isDark ? '#f0c95a' : 'var(--lp-gold)', fontWeight: 600 }}>Learn. Connect. Create.</span>
        </motion.p>

        {/* Equalizer flourish */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.28 }}
          className="mt-3"
        >
          <EqualizerMark isDark={isDark} />
        </motion.div>

        {/* Supporting line — small, refined */}
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.34 }}
          data-testid="hero-subheadline"
          className="mt-2.5 text-[11px] sm:text-[12px] font-semibold uppercase lp-muted-strong lp-hero-text"
          style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '0.18em', opacity: 0.65 }}
        >
          One Platform · Endless Possibilities
        </motion.p>
      </div>

      {/* ── 4 destination cards (responsive: 4 cols on desktop, 2x2 on mobile) ── */}
      <div className="relative flex-1 max-w-6xl w-full mx-auto px-4 sm:px-5 flex items-center justify-center min-h-0">
        <motion.div
          initial="initial" animate="animate" variants={stagger}
          className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 w-full"
          data-testid="hero-cards-grid"
        >
          <motion.div variants={fadeUp}>
            <HeroDestCard
              testid="hero-card-student"
              index="01"
              icon={GraduationCap}
              title="Students"
              description="Learn from world-class instructors, courses & resources."
              ctaLabel="Enroll Now"
              isDark={isDark}
              onClick={onStartLearning}
            />
          </motion.div>
          <motion.div variants={fadeUp}>
            <HeroDestCard
              testid="hero-card-instructor"
              index="02"
              icon={UserIcon}
              title="Instructors"
              description="Build your academy and teach students across the globe."
              ctaLabel="Start Teaching"
              isDark={isDark}
              onClick={onInstructor}
            />
          </motion.div>
          <motion.div variants={fadeUp}>
            <HeroDestCard
              testid="hero-card-community"
              index="03"
              icon={MessageSquare}
              title="Community"
              description="Connect with musicians worldwide. Share & collaborate."
              ctaLabel="Join Free"
              isDark={isDark}
              onClick={onCommunity}
            />
          </motion.div>
          <motion.div variants={fadeUp}>
            <HeroDestCard
              testid="hero-card-textbook-studio"
              index="04"
              icon={BookOpen}
              title="Textbook Studio"
              description="AI-powered textbook generator. Create. Educate. Elevate."
              ctaLabel="Try It Free"
              isDark={isDark}
              onClick={() => navigate('/studio')}
              badge="NEW"
            />
          </motion.div>
        </motion.div>
      </div>

      {/* Bottom fade for next section */}
      <div aria-hidden className="absolute inset-x-0 bottom-0 h-24 sm:h-32 pointer-events-none"
        style={{
          background: isDark
            ? 'linear-gradient(180deg, rgba(7,7,11,0) 0%, rgba(7,7,11,0.55) 45%, hsl(var(--background)) 100%)'
            : 'linear-gradient(180deg, rgba(252,248,240,0) 0%, rgba(252,248,240,0.70) 45%, hsl(var(--background)) 100%)',
        }}
      />

      {/* Scroll hint (compact) */}
      <motion.div
        aria-hidden
        animate={{ y: [0, 6, 0], opacity: [0.4, 0.8, 0.4] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
        className="relative mt-3 sm:mt-4 flex flex-col items-center gap-1 pb-2 shrink-0"
        data-testid="hero-scroll-hint"
      >
        <div className="w-5 h-8 rounded-full border-2 flex justify-center pt-1"
          style={{ borderColor: isDark ? 'rgba(255,255,255,0.30)' : 'rgba(0,0,0,0.30)' }}>
          <div className="w-[2px] h-1.5 rounded-full"
            style={{ background: isDark ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.55)' }} />
        </div>
        <span className="text-[9px] uppercase tracking-[0.22em] font-semibold"
              style={{ color: isDark ? 'rgba(255,255,255,0.50)' : 'rgba(0,0,0,0.50)' }}>
          Scroll Down
        </span>
      </motion.div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 3. Portal Cards — Community is the hero (double-wide on desktop)
// ═══════════════════════════════════════════════════════════════════
function PortalCard({ to, role, image, icon: Icon, eyebrow, title, description, cta, accent, primary, testId, onClick, secondaryCta, onSecondaryClick }) {
  return (
    <div
      data-testid={testId}
      className={`group relative text-left rounded-[28px] overflow-hidden transition-all duration-500 hover:scale-[1.02] hover:-translate-y-1 ${primary ? 'lg:col-span-2 lg:row-span-2 min-h-[420px] lg:min-h-[560px]' : 'min-h-[280px] lg:min-h-[270px]'}`}
      style={{
        background: 'rgba(8,8,10,0.55)',
        border: '1px solid rgba(255,255,255,0.08)',
        backdropFilter: 'blur(24px) saturate(1.5)',
        WebkitBackdropFilter: 'blur(24px) saturate(1.5)',
        boxShadow: '0 18px 60px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.04)',
      }}
    >
      {/* Click-through layer — covers the whole card except the secondary CTA */}
      <button
        type="button"
        onClick={() => onClick(role)}
        data-testid={`${testId}-main`}
        aria-label={`Open ${title}`}
        className="absolute inset-0 z-10 cursor-pointer"
        style={{ background: 'transparent', border: 'none' }}
      />

      {/* Background image with parallax glow */}
      <img
        src={image}
        alt=""
        loading="lazy"
        decoding="async"
        width={800}
        height={600}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] group-hover:scale-110"
        style={{ filter: 'saturate(1.05) brightness(0.55)' }}
      />
      <div className="absolute inset-0"
        style={{
          background: `linear-gradient(160deg, ${accent.veil} 0%, rgba(7,7,11,0.55) 60%, rgba(7,7,11,0.95) 100%)`,
        }}
      />
      {/* Corner accent glow */}
      <div aria-hidden className="absolute -top-16 -right-12 w-64 h-64 rounded-full pointer-events-none transition-opacity duration-500 group-hover:opacity-90"
        style={{ background: `radial-gradient(circle, ${accent.glow}, transparent 70%)`, filter: 'blur(28px)', opacity: 0.65 }} />

      {/* Content */}
      <div className={`relative h-full flex flex-col justify-between p-6 sm:p-7 ${primary ? 'lg:p-10' : ''} pointer-events-none`}>
        <div>
          <div className="flex items-start justify-between mb-4">
            <div className={`rounded-2xl flex items-center justify-center shrink-0 ${primary ? 'w-14 h-14 lg:w-16 lg:h-16' : 'w-11 h-11'}`}
              style={{
                background: accent.iconBg,
                border: `1px solid ${accent.iconBorder}`,
                boxShadow: `0 8px 26px ${accent.glow}`,
              }}>
              <Icon size={primary ? 26 : 18} style={{ color: accent.icon }} />
            </div>
            {primary && (
              <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.22em]"
                style={{
                  background: 'rgba(212,175,55,0.20)',
                  color: '#fcd34d',
                  border: '1px solid rgba(212,175,55,0.45)',
                  backdropFilter: 'blur(8px)',
                }}>
                Most Active
              </span>
            )}
          </div>
          <p className="text-[10px] font-bold uppercase tracking-[0.24em] opacity-65 mb-2"
            style={{ color: accent.eyebrow, fontFamily: 'Outfit, sans-serif' }}>
            {eyebrow}
          </p>
          <h3 className={`font-bold leading-tight ${primary ? 'text-3xl sm:text-4xl lg:text-5xl mb-3' : 'text-xl sm:text-2xl mb-2'}`}
            style={{ fontFamily: 'Playfair Display, serif', color: '#fff' }}>
            {title}
          </h3>
          <p className={`opacity-80 ${primary ? 'text-base lg:text-lg max-w-md' : 'text-sm'}`}
            style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255,255,255,0.85)' }}>
            {description}
          </p>
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-2.5 pointer-events-none">
          <button
            type="button"
            onClick={() => onClick(role)}
            data-testid={`${testId}-cta`}
            className={`inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-[12px] font-bold transition-all duration-300 hover:gap-3 hover:scale-[1.03] pointer-events-auto relative z-20 ${primary ? 'lg:text-sm lg:py-3 lg:px-6' : ''}`}
            style={{
              background: primary ? 'linear-gradient(135deg, #E5C158, #D4AF37, #b8942e)' : 'rgba(255,255,255,0.08)',
              color: primary ? '#0a0a0a' : '#fff',
              border: primary ? '1px solid rgba(212,175,55,0.5)' : '1px solid rgba(255,255,255,0.18)',
              backdropFilter: primary ? 'none' : 'blur(10px)',
              boxShadow: primary ? '0 8px 28px rgba(212,175,55,0.45)' : 'none',
              fontFamily: 'Outfit, sans-serif',
            }}
          >
            {cta} <ArrowRight size={primary ? 14 : 12} />
          </button>

          {secondaryCta && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onSecondaryClick && onSecondaryClick(); }}
              data-testid={`${testId}-secondary-cta`}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[12px] font-semibold transition-all duration-300 hover:scale-[1.04] pointer-events-auto relative z-20"
              style={{
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.18)',
                color: '#fff',
                backdropFilter: 'blur(12px)',
                fontFamily: 'Outfit, sans-serif',
              }}
            >
              <span className="relative w-5 h-5 rounded-full flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, rgba(244,63,94,0.5), rgba(212,175,55,0.5))',
                  border: '1px solid rgba(255,255,255,0.22)',
                }}
              >
                <Play size={9} fill="currentColor" className="text-white ml-[1px]" />
              </span>
              {secondaryCta}
              <span className="opacity-65 text-[10px]">· 60s</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PortalsSection({ onPortalClick, onTrailerClick }) {
  return (
    <section id="portals" data-testid="landing-portals-section" className="relative py-16 sm:py-24 lg:py-32 px-4 sm:px-6 lg:px-8">
      {/* Section ambient */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80%] h-32"
          style={{ background: 'radial-gradient(ellipse at center, rgba(212,175,55,0.12), transparent 70%)', filter: 'blur(40px)' }} />
      </div>
      <div className="max-w-7xl mx-auto relative">
        <div className="text-center mb-10 sm:mb-14">
          <p className="text-[10px] uppercase tracking-[0.28em] font-bold lp-eyebrow mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Choose your room
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Three portals. <span style={{ color: 'var(--lp-gold)' }}>One studio.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5">
          <PortalCard
            role="community"
            testId="portal-card-community"
            image={PORTALS_BG.community}
            icon={Radio}
            eyebrow="Live · Gift · Collab"
            title="Community Portal"
            description="Go Live • Watch Lives • Send Gifts • Collab with musicians from around the world. This is where the action happens."
            cta="Enter Community Portal"
            accent={{
              veil: 'rgba(212,175,55,0.45)',
              glow: 'rgba(212,175,55,0.55)',
              icon: '#0a0a0a',
              iconBg: 'linear-gradient(135deg, #E5C158, #D4AF37)',
              iconBorder: 'rgba(212,175,55,0.6)',
              eyebrow: '#fcd34d',
            }}
            onClick={onPortalClick}
          />
          <PortalCard
            role="student"
            testId="portal-card-student"
            image={PORTALS_BG.student}
            icon={GraduationCap}
            eyebrow="Learn · Practice"
            title="Student Portal"
            description="Structured lessons across 21 disciplines, plus practice tools and 1-on-1 coaching."
            cta="Start Learning"
            accent={{
              veil: 'rgba(34,211,238,0.30)',
              glow: 'rgba(34,211,238,0.45)',
              icon: '#7dd3fc',
              iconBg: 'rgba(34,211,238,0.16)',
              iconBorder: 'rgba(34,211,238,0.4)',
              eyebrow: '#7dd3fc',
            }}
            onClick={onPortalClick}
          />
          <PortalCard
            role="instructor"
            testId="portal-card-instructor"
            image={PORTALS_BG.instructor}
            icon={Mic2}
            eyebrow="Teach · Earn"
            title="Instructor Portal"
            description="Teach students, set your rates, run live classes, and grow your income."
            cta="Become an Instructor"
            accent={{
              veil: 'rgba(244,63,94,0.30)',
              glow: 'rgba(244,63,94,0.45)',
              icon: '#fda4af',
              iconBg: 'rgba(244,63,94,0.18)',
              iconBorder: 'rgba(244,63,94,0.4)',
              eyebrow: '#fda4af',
            }}
            onClick={onPortalClick}
          />
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 4. Instructors — every instructor from /api/instructors/directory in an
//    infinite right-to-left marquee (draggable, pauses on hover). When the
//    directory returns fewer than 6 (dev/test), 10 mocked instructors pad
//    the loop so the animation can be exercised before production data.
// ═══════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════
// Main exported component
// ═══════════════════════════════════════════════════════════════════
export default function LandingPage() {
  const { theme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [authRole, setAuthRole] = useState(null);
  const [authInitialMode, setAuthInitialMode] = useState('login');
  const [trailerOpen, setTrailerOpen] = useState(false);

  // Pre-open registration if URL signals a referral
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('signup') === 'student' && !user) {
      setAuthRole('student');
      setAuthInitialMode('register');
      window.history.replaceState({}, '', '/');
    }
  }, [user]);

  const portalRoutes = { admin: '/admin', instructor: '/instructor', student: '/student', community: '/community' };

  const handlePortalClick = (role) => {
    if (user) {
      navigate(portalRoutes[role] || '/student');
    } else {
      setAuthInitialMode('register');
      setAuthRole(role);
    }
  };

  // Triggered from the navbar Login dropdown — one entry per portal.
  // Admin uses the dedicated /admin route (password-gated, no AuthModal).
  // Other portals open the AuthModal pre-set to login mode for that role.
  // IMPORTANT: respect the explicit portal choice from the dropdown. Previously
  // we navigated logged-in users straight to their own role's portal, which
  // meant a stale admin login sent every "Student / Instructor / Community"
  // click to /admin. Now we honor the role the user actually clicked.
  const handlePortalLogin = (role) => {
    if (role === 'admin') {
      navigate('/admin');
      return;
    }
    if (user) {
      navigate(portalRoutes[role] || '/student');
      return;
    }
    setAuthInitialMode('login');
    setAuthRole(role);
  };

  // Triggered from the navbar Disciplines dropdown. Empty value = browse all.
  const handleDiscipline = (course) => {
    if (course) {
      navigate(`/instructors?course=${encodeURIComponent(course)}`);
    } else {
      navigate('/instructors');
    }
  };

  const onAuthSuccess = (userData) => {
    const role = userData?.role || authRole || 'student';
    setAuthRole(null);
    navigate(portalRoutes[role] || '/student');
  };

  const logo = theme === 'light' ? mmsLogoLight : mmsLogo;

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-background themed-page-bg landing-page-shell">
      <Seo path="/" />
      <Suspense fallback={null}>
      <AnimatePresence>
        {authRole && authRole !== 'community' && authRole !== 'community-email' && (
          <AuthModal
            role={authRole}
            initialMode={authInitialMode}
            onClose={() => { setAuthRole(null); setAuthInitialMode('login'); }}
            onSuccess={onAuthSuccess}
          />
        )}
        {authRole === 'community' && (
          <CommunityAuthModal
            onClose={() => { setAuthRole(null); setAuthInitialMode('login'); }}
            onSuccess={onAuthSuccess}
            openEmailForm={() => setAuthRole('community-email')}
          />
        )}
        {authRole === 'community-email' && (
          <AuthModal
            role="community"
            initialMode={authInitialMode}
            onClose={() => { setAuthRole(null); setAuthInitialMode('login'); }}
            onSuccess={onAuthSuccess}
          />
        )}
      </AnimatePresence>
      </Suspense>

      <AnimatePresence>
        {trailerOpen && <TrailerLightbox onClose={() => setTrailerOpen(false)} />}
      </AnimatePresence>

      <Navbar
        logo={logo}
        onPortalLogin={handlePortalLogin}
        onPortalClick={handlePortalClick}
        onDiscipline={handleDiscipline}
      />
      <Hero
        onCommunity={() => handlePortalClick('community')}
        onStartLearning={() => handlePortalClick('student')}
        onInstructor={() => handlePortalClick('instructor')}
      />
      <Suspense fallback={<div style={{ minHeight: '100vh' }} aria-hidden />}>
        <BelowFold logo={logo} onBrowseAll={() => navigate('/instructors')} onPickDiscipline={handleDiscipline} />
      </Suspense>
    </div>
  );
}
